import React, { useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import {
  Hash,
  Calculator,
  ChevronRight,
  Home,
  Info,
  BookOpen,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  Copy,
  Check,
  GitBranch,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Helper functions
const isPrime = (num) => {
  if (num < 2) return false;
  if (num === 2) return true;
  if (num % 2 === 0) return false;
  
  for (let i = 3; i <= Math.sqrt(num); i += 2) {
    if (num % i === 0) return false;
  }
  return true;
};

const getPrimeFactors = (num) => {
  const factors = [];
  let n = Math.abs(num);
  
  // Handle 2 separately
  while (n % 2 === 0) {
    factors.push(2);
    n = n / 2;
  }
  
  // Check odd numbers
  for (let i = 3; i <= Math.sqrt(n); i += 2) {
    while (n % i === 0) {
      factors.push(i);
      n = n / i;
    }
  }
  
  // If n is still greater than 2, it's a prime factor
  if (n > 2) factors.push(n);
  
  return factors;
};

const getFactorPairs = (num) => {
  const pairs = [];
  const n = Math.abs(num);
  
  for (let i = 1; i <= Math.sqrt(n); i++) {
    if (n % i === 0) {
      pairs.push([i, n / i]);
    }
  }
  
  return pairs;
};

const getAllFactors = (num) => {
  const factors = new Set();
  const n = Math.abs(num);
  
  for (let i = 1; i <= Math.sqrt(n); i++) {
    if (n % i === 0) {
      factors.add(i);
      factors.add(n / i);
    }
  }
  
  return Array.from(factors).sort((a, b) => a - b);
};

const getNearestPrimes = (num) => {
  let lower = num - 1;
  let upper = num + 1;
  
  // Find lower prime
  while (lower > 1 && !isPrime(lower)) {
    lower--;
  }
  
  // Find upper prime
  while (!isPrime(upper)) {
    upper++;
  }
  
  return { lower: lower > 1 ? lower : null, upper };
};

// Factor Tree Visualization
const FactorTree = ({ number }) => {
  const factors = getPrimeFactors(number);
  
  if (factors.length === 0) return null;
  
  const buildTree = (n, depth = 0) => {
    if (isPrime(n) || n === 1) {
      return (
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-sm font-bold text-primary">{n}</span>
          </div>
        </div>
      );
    }
    
    // Find first factor
    let factor = 2;
    while (n % factor !== 0 && factor <= Math.sqrt(n)) {
      factor++;
    }
    
    if (n % factor !== 0) factor = n;
    const other = n / factor;
    
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
          <span className="text-sm font-bold text-foreground">{n}</span>
        </div>
        <div className="flex gap-4 items-start">
          <div className="flex flex-col items-center">
            <div className="w-0.5 h-8 bg-border"></div>
            {buildTree(factor, depth + 1)}
          </div>
          <div className="flex flex-col items-center">
            <div className="w-0.5 h-8 bg-border"></div>
            {buildTree(other, depth + 1)}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="flex justify-center p-6 overflow-x-auto">
      {buildTree(number)}
    </div>
  );
};

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Main Calculator Component
export default function PrimeCalculator() {
  const [number, setNumber] = useState(24);
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  // Calculate all values
  const results = useMemo(() => {
    const n = Math.abs(number);
    const primeCheck = isPrime(n);
    const primeFactors = getPrimeFactors(n);
    const allFactors = getAllFactors(n);
    const factorPairs = getFactorPairs(n);
    const nearestPrimes = getNearestPrimes(n);
    
    // Count occurrences of each prime factor
    const factorCount = {};
    primeFactors.forEach(f => {
      factorCount[f] = (factorCount[f] || 0) + 1;
    });
    
    // Prime factorization in exponential form
    const factorizationStr = Object.entries(factorCount)
      .map(([base, exp]) => exp > 1 ? `${base}^${exp}` : base)
      .join(' × ');
    
    return {
      isPrime: primeCheck,
      primeFactors,
      allFactors,
      factorPairs,
      factorCount,
      factorizationStr,
      nearestPrimes,
      numFactors: allFactors.length,
    };
  }, [number]);

  return (
    <>
      <Helmet>
        <title>Prime Number & Factorization Calculator | MathCalcPro</title>
        <meta name="description" content="Free prime number checker and factorization tool. Check if a number is prime, find prime factors, factor pairs, and visualize factor trees with step-by-step solutions." />
        <meta name="keywords" content="prime number calculator, prime checker, prime factorization, factor tree, composite numbers" />
        <link rel="canonical" href="https://mathcalcpro.com/calculators/prime" />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6">
          <Link to="/" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
            <Home className="w-3 h-3" />
            <span>Home</span>
          </Link>
          <ChevronRight className="w-3 h-3 text-muted-foreground" />
          <span className="text-foreground font-medium">Prime & Factorization</span>
        </nav>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Hash className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Prime & Factorization Tool</h1>
              <p className="text-muted-foreground">Check primality and find all factors</p>
            </div>
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Calculator Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:sticky lg:top-24 lg:self-start"
          >
            <Card className="glass-strong rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <Calculator className="w-5 h-5 text-primary" />
                <span className="font-semibold text-foreground">Calculator</span>
              </div>

              {/* Number Input */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Enter Number</Label>
                  <Input
                    type="number"
                    value={number}
                    onChange={(e) => setNumber(parseInt(e.target.value) || 0)}
                    className="neumorph-inset border-0 text-xl font-mono h-14"
                    placeholder="24"
                  />
                </div>

                {/* Quick Numbers */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Quick Numbers</Label>
                  <div className="flex flex-wrap gap-2">
                    {[2, 7, 12, 17, 24, 30, 49, 100].map((n) => (
                      <Button
                        key={n}
                        variant="outline"
                        size="sm"
                        className={`text-xs rounded-lg ${number === n ? 'bg-primary text-primary-foreground' : ''}`}
                        onClick={() => setNumber(n)}
                      >
                        {n}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Prime Check Result */}
                <div className="pt-4 border-t border-border">
                  <div className="neumorph-sm rounded-xl p-4 text-center">
                    <Badge
                      variant={results.isPrime ? 'default' : 'secondary'}
                      className="text-lg px-4 py-2"
                    >
                      {results.isPrime ? '✓ Prime Number' : '✗ Composite Number'}
                    </Badge>
                    {!results.isPrime && number > 1 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Has {results.numFactors} factors
                      </p>
                    )}
                  </div>
                </div>

                {/* Results Summary */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-foreground">Quick Info</Label>
                  
                  {/* Prime Factorization */}
                  <div className="neumorph-sm rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">Prime Factorization</span>
                      <CopyButton text={results.factorizationStr || 'N/A'} />
                    </div>
                    <span className="font-mono text-sm font-semibold text-foreground">
                      {results.factorizationStr || 'N/A'}
                    </span>
                  </div>

                  {/* All Factors */}
                  <div className="neumorph-sm rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">All Factors ({results.numFactors})</span>
                      <CopyButton text={results.allFactors.join(', ')} />
                    </div>
                    <span className="font-mono text-sm text-foreground">
                      {results.allFactors.join(', ')}
                    </span>
                  </div>

                  {/* Nearest Primes */}
                  <div className="neumorph-sm rounded-xl p-3">
                    <span className="text-xs text-muted-foreground block mb-1">Nearest Primes</span>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Previous:</span>
                      <span className="font-mono font-semibold text-foreground">
                        {results.nearestPrimes.lower || 'None'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Next:</span>
                      <span className="font-mono font-semibold text-foreground">
                        {results.nearestPrimes.upper}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Center/Right Column - Visualization & Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visualization Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-foreground flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-primary" />
                    Factor Tree Visualization
                  </h2>
                </div>

                {!results.isPrime && number > 1 ? (
                  <div className="neumorph-sm rounded-xl overflow-hidden">
                    <FactorTree number={number} />
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    {number === 1 ? '1 is neither prime nor composite' : 'Prime numbers have no factor tree'}
                  </div>
                )}

                {/* Factor Pairs */}
                {!results.isPrime && number > 1 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-foreground mb-3">Factor Pairs</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {results.factorPairs.map((pair, i) => (
                        <div key={i} className="bg-muted/30 rounded-lg p-2 text-center">
                          <span className="font-mono text-sm text-foreground">
                            {pair[0]} × {pair[1]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            </motion.div>

            {/* Step-by-Step Explanation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="glass rounded-2xl p-6">
                <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                  <BookOpen className="w-4 h-4 text-primary" />
                  Step-by-Step Analysis
                </h2>

                <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
                  <AccordionItem value="step-1" className="border-b border-border">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="step-badge">1</span>
                        <span className="font-medium">Check if prime</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-11 space-y-2">
                        <p className="text-muted-foreground">
                          Checking number: <span className="font-mono text-foreground font-semibold">{number}</span>
                        </p>
                        {results.isPrime ? (
                          <p className="text-muted-foreground">
                            This number is <span className="text-primary font-semibold">PRIME</span> because it has exactly two factors: 1 and {number}.
                          </p>
                        ) : (
                          <p className="text-muted-foreground">
                            This number is <span className="text-secondary font-semibold">COMPOSITE</span> because it has more than two factors.
                          </p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="step-2" className="border-b border-border">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="step-badge">2</span>
                        <span className="font-medium">Find prime factorization</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-11 space-y-3">
                        <p className="text-muted-foreground">
                          Breaking down {number} into prime factors:
                        </p>
                        {results.primeFactors.length > 0 ? (
                          <>
                            <div className="formula-display">
                              {number} = {results.primeFactors.join(' × ')}
                            </div>
                            <p className="text-muted-foreground">
                              In exponential form: <span className="font-mono text-foreground font-semibold">{results.factorizationStr}</span>
                            </p>
                          </>
                        ) : (
                          <p className="text-muted-foreground">This number is prime, so it cannot be factored further.</p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="step-3" className="border-0">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="step-badge">3</span>
                        <span className="font-medium">List all factors</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-11 space-y-2">
                        <p className="text-muted-foreground">
                          All factors of {number}:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {results.allFactors.map((f, i) => (
                            <Badge key={i} variant="secondary" className="font-mono">
                              {f}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-muted-foreground">
                          Total: <span className="font-semibold text-foreground">{results.numFactors}</span> factors
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            </motion.div>

            {/* Deep Dive Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-6"
            >
              {/* What are Prime Numbers */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">What are Prime Numbers?</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  A prime number is a natural number greater than 1 that has no positive divisors other than 1 and itself. 
                  In other words, it can only be divided evenly by 1 and the number itself. The first few prime numbers are 2, 3, 5, 7, 11, 13, and so on.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Composite numbers, on the other hand, have more than two factors. For example, 12 is composite because it can be divided 
                  by 1, 2, 3, 4, 6, and 12. The number 1 is special—it's neither prime nor composite.
                </p>
              </Card>

              {/* Prime Properties */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary" />
                  Key Properties of Prime Numbers
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { name: 'Fundamental Theorem', desc: 'Every integer > 1 has unique prime factorization' },
                    { name: 'Infinity', desc: 'There are infinitely many prime numbers' },
                    { name: 'Only Even Prime', desc: '2 is the only even prime number' },
                    { name: 'Prime Gaps', desc: 'The gap between consecutive primes can be arbitrarily large' },
                  ].map((item, i) => (
                    <div key={i} className="bg-muted/30 rounded-xl p-4">
                      <span className="text-sm font-semibold text-foreground block mb-1">{item.name}</span>
                      <span className="text-xs text-muted-foreground">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Real World Applications */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">Real-World Applications</h2>
                <ul className="space-y-3">
                  {[
                    'Cryptography: RSA encryption relies on the difficulty of factoring large prime numbers',
                    'Computer Science: Hash tables and algorithms use primes for efficient data distribution',
                    'Cicadas: Some cicada species emerge in prime-numbered year cycles (13, 17 years)',
                    'Music: Prime number rhythms create interesting polyrhythmic patterns',
                    'Random Number Generation: Primes are used in pseudorandom number generators',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <ArrowRight className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* Common Mistakes */}
              <Card className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-destructive" />
                  Common Mistakes to Avoid
                </h2>
                <ul className="space-y-3">
                  {[
                    "Thinking 1 is a prime number (it's neither prime nor composite)",
                    "Forgetting that 2 is prime (it's the only even prime number)",
                    "Confusing \"prime factorization\" with \"all factors\" (prime factorization only includes primes)",
                    "Not checking divisibility up to √n when testing for primality",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* FAQ Section */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
                <Accordion type="single" collapsible className="w-full">
                  {[
                    {
                      q: 'Why is 1 not considered prime?',
                      a: 'By definition, a prime number must have exactly two distinct factors: 1 and itself. The number 1 only has one factor (itself), so it doesn\'t meet the definition. This convention helps maintain the uniqueness of prime factorization.'
                    },
                    {
                      q: 'What is the largest known prime number?',
                      a: 'As of 2024, the largest known prime is 2^82,589,933 − 1, which has over 24 million digits! It was discovered as part of the Great Internet Mersenne Prime Search (GIMPS) project.'
                    },
                    {
                      q: 'How do you test if a large number is prime?',
                      a: 'For large numbers, we use probabilistic tests like the Miller-Rabin primality test. These tests can quickly determine if a number is "probably prime" with very high confidence, which is sufficient for most applications including cryptography.'
                    },
                    {
                      q: 'What is prime factorization used for?',
                      a: 'Prime factorization is fundamental in number theory and has many applications: finding GCD and LCM, simplifying fractions, solving Diophantine equations, and most importantly, modern cryptography (RSA encryption).'
                    },
                    {
                      q: 'Are there patterns in prime numbers?',
                      a: 'While primes appear random, there are fascinating patterns. Twin primes (like 11 and 13) differ by 2, prime gaps vary, and the Prime Number Theorem describes their approximate distribution. However, no simple formula generates all primes.'
                    }
                  ].map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                      <AccordionTrigger className="text-left hover:no-underline">
                        <span className="font-medium text-foreground">{faq.q}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}